import { useCallback, useState, useRef, type CSSProperties } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type { DockItem as DockItemType } from '../../types/dock';
import { useDock } from './DockContext';

/**
 * Props for DockItem component
 */
interface DockItemProps {
  /** The dock item data */
  item: DockItemType;
  /** Click handler for the item */
  onClick: () => void;
  /** Whether this item is in the drawer (vs main dock) */
  inDrawer?: boolean;
  /** Whether drag and drop is enabled */
  isDragEnabled?: boolean;
  /** Magnification scale from parent (for macOS-style effect) */
  magnificationScale?: number;
  /** Whether this item is currently active/selected */
  isActive?: boolean;
  /** Callback when mouse enters the item */
  onHover?: () => void;
  /** Callback when mouse leaves the item */
  onLeave?: () => void;
}

/**
 * Props for the tooltip component
 */
interface TooltipProps {
  /** Text to display in tooltip */
  label: string;
  /** Whether tooltip is visible */
  isVisible: boolean;
}

/**
 * Props for ripple effect
 */
interface RippleState {
  key: number;
  x: number;
  y: number;
}

/**
 * Tooltip component for dock items
 */
function Tooltip({ label, isVisible }: TooltipProps): JSX.Element | null {
  if (!isVisible) {return null;}

  return (
    <div className="dock-tooltip" role="tooltip">
      {label}
    </div>
  );
}

/**
 * Ripple effect component
 */
function Ripple({ ripples }: { ripples: RippleState[] }): JSX.Element {
  return (
    <>
      {ripples.map((ripple) => (
        <span
          key={ripple.key}
          className="dock-item-ripple"
          style={{
            left: `${ripple.x}%`,
            top: `${ripple.y}%`,
          }}
        />
      ))}
    </>
  );
}

/**
 * Individual dock item component
 * Displays an icon with tooltip and handles click/drag interactions
 * Supports magnification, ripple effects, and active state indicators
 */
export function DockItem({
  item,
  onClick,
  inDrawer = false,
  isDragEnabled = false,
  magnificationScale = 1,
  isActive = false,
  onHover,
  onLeave,
}: DockItemProps): JSX.Element {
  const { isEditing, removeItem } = useDock();
  const [showTooltip, setShowTooltip] = useState(false);
  const [ripples, setRipples] = useState<RippleState[]>([]);
  const rippleCounter = useRef(0);
  const buttonRef = useRef<HTMLButtonElement>(null);

  // Set up sortable functionality from dnd-kit
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: item.id,
    disabled: !isDragEnabled || !isEditing,
  });

  const style: CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    // Set magnification scale as CSS custom property
    '--dock-magnification-scale': magnificationScale,
  } as CSSProperties;

  const handleMouseEnter = useCallback(() => {
    if (!isDragging) {
      setShowTooltip(true);
      onHover?.();
    }
  }, [isDragging, onHover]);

  const handleMouseLeave = useCallback(() => {
    setShowTooltip(false);
    onLeave?.();
  }, [onLeave]);

  const handleClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      if (!isEditing) {
        // Create ripple effect at click position
        const button = buttonRef.current;
        if (button) {
          const rect = button.getBoundingClientRect();
          const x = ((event.clientX - rect.left) / rect.width) * 100;
          const y = ((event.clientY - rect.top) / rect.height) * 100;

          const newRipple: RippleState = {
            key: rippleCounter.current++,
            x,
            y,
          };

          setRipples((prev) => [...prev, newRipple]);

          // Remove ripple after animation completes
          setTimeout(() => {
            setRipples((prev) => prev.filter((r) => r.key !== newRipple.key));
          }, 500);
        }

        onClick();
      }
    },
    [isEditing, onClick]
  );

  const handleRemove = useCallback(
    (event: React.MouseEvent) => {
      event.stopPropagation();
      if (item.type === 'custom') {
        removeItem(item.id);
      }
    },
    [item.id, item.type, removeItem]
  );

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        if (!isEditing) {
          // Create centered ripple for keyboard activation
          const newRipple: RippleState = {
            key: rippleCounter.current++,
            x: 50,
            y: 50,
          };

          setRipples((prev) => [...prev, newRipple]);

          setTimeout(() => {
            setRipples((prev) => prev.filter((r) => r.key !== newRipple.key));
          }, 500);

          onClick();
        }
      }
    },
    [isEditing, onClick]
  );

  const canRemove = isEditing && item.type === 'custom';

  // Build CSS classes
  const classNames = [
    'dock-item',
    inDrawer ? 'dock-item--drawer' : '',
    isDragging ? 'dock-item--dragging' : '',
    isEditing ? 'dock-item--editing' : '',
    isActive ? 'dock-item--active' : '',
    item.type === 'system' ? 'dock-item--system' : 'dock-item--custom',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={classNames}
      {...(isDragEnabled && isEditing ? { ...attributes, ...listeners } : {})}
    >
      <button
        ref={buttonRef}
        type="button"
        className="dock-item-button"
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onFocus={handleMouseEnter}
        onBlur={handleMouseLeave}
        aria-label={item.label}
        disabled={isDragging}
      >
        <span className="dock-item-icon" aria-hidden="true">
          {item.icon}
        </span>

        {/* Ripple effects */}
        <Ripple ripples={ripples} />

        {/* Remove button for custom items in edit mode */}
        {canRemove && (
          <button
            type="button"
            className="dock-item-remove"
            onClick={handleRemove}
            aria-label={`Remove ${item.label}`}
          >
            <span aria-hidden="true">x</span>
          </button>
        )}

        {/* Tooltip - only show when not in drawer */}
        {!inDrawer && <Tooltip label={item.label} isVisible={showTooltip} />}
      </button>

      {/* Label shown in drawer view */}
      {inDrawer && <span className="dock-item-label">{item.label}</span>}
    </div>
  );
}

export default DockItem;
